import React, { useCallback, useMemo, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { IconButton, makeStyles, Snackbar, Tooltip } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import EditIcon from '@material-ui/icons/Edit'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import LocalOfferIcon from '@material-ui/icons/LocalOffer'
import SydButton from '../../../../../commonDesign/Button'
import AdminPage from '../../../../admin/shared/AdminPage'
import OperationalTable from '../../../../../organisms/OperationalTable'
import Loading from '../../../../../molecules/Loading'
import ErrorDisplay from '../../../../../molecules/ErrorDisplay'
import { dataPipelineVersioning } from '../../../../../../api/coreData'
import { formatRelativeTime } from '../../SmartEndpoints/formatRelativeTime'
import { ADMIN_ROUTES } from '../../../../../../constants'
import DataPipelineTagManagementModal from './DataPipelineTagManagementModal'

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px var(--padding-md)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  pipelineCount: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  searchInput: {
    padding: '5px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    outline: 'none',
    width: '200px',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': { borderColor: 'var(--color-primary-400)', boxShadow: '0 0 0 2px var(--color-primary-100)' },
    '&::placeholder': { color: 'var(--color-neutral-400)' }
  },
  tableWrap: {
    '& .operational-table > thead > tr > th': {
      fontSize: 'var(--text-size-xs)',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: 'var(--color-neutral-500)',
      padding: '10px 18px',
      borderBottom: '1px solid var(--color-neutral-200)'
    },
    '& .operational-table > thead': { backgroundColor: 'var(--color-neutral-050)' },
    '& .operational-table > tbody > tr > td': { padding: '10px 18px' }
  },
  mono: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 'var(--text-size-xs)'
  },
  versionBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-600)'
  },
  liveBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)'
  },
  notLiveBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 500,
    fontStyle: 'italic',
    backgroundColor: 'var(--color-neutral-050)',
    color: 'var(--color-neutral-400)'
  },
  driftIndicator: {
    marginLeft: '6px',
    fontSize: '11px',
    color: 'var(--color-warning-600, #d97706)',
    fontWeight: 500
  },
  actionBtn: {
    color: 'var(--color-neutral-300)',
    transition: 'color 0.15s ease',
    '&:hover': {
      color: 'var(--color-primary-600)',
      backgroundColor: 'var(--color-primary-050, #eff6ff)'
    }
  },
  timestampText: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-400)'
  },
  newBtnWrap: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '6px 0'
  }
}))

function DataPipelinesHome () {
  const classes = useStyles()
  const history = useHistory()
  const [searchText, setSearchText] = useState('')
  const [tagModalCode, setTagModalCode] = useState(null)
  const [toast, setToast] = useState(null)

  const { data, isLoading, error, refetch } = dataPipelineVersioning.useListDataPipelineSummaries()

  const tableData = useMemo(() => data?.summaries || [], [data])

  const handleDuplicate = useCallback((pipeline) => {
    const baseName = pipeline.displayName ? `${pipeline.displayName} (copy)` : ''
    const params = new URLSearchParams({
      duplicateFrom: pipeline.code,
      name: baseName
    })
    history.push(`${ADMIN_ROUTES.DENALI.ANALYSIS.NEW_DATA_PIPELINE}?${params.toString()}`)
    setToast({ message: `Duplicating "${pipeline.code}" — fill in a new code and save`, severity: 'info' })
  }, [history])

  /* eslint-disable react/prop-types */
  const columns = useMemo(() => [
    {
      id: 'code',
      accessor: 'code',
      Header: 'Code',
      Cell: ({ value }) => <span className={classes.mono}>{value}</span>
    },
    { id: 'displayName', accessor: 'displayName', Header: 'Display Name' },
    {
      id: 'latestVersion',
      accessor: 'latestVersion',
      Header: 'Latest',
      Cell: ({ value }) => <span className={classes.versionBadge}>v{value}</span>
    },
    {
      id: 'liveVersion',
      accessor: 'liveVersion',
      Header: 'Live',
      Cell: ({ row }) => {
        const live = row.original.liveVersion
        const latest = row.original.latestVersion
        if (live == null) return <span className={classes.notLiveBadge}>not live</span>
        return (
          <span>
            <span className={classes.liveBadge}>v{live}</span>
            {live < latest && (
              <Tooltip title={`Latest is v${latest} (drift)`} arrow>
                <span className={classes.driftIndicator}>· drift</span>
              </Tooltip>
            )}
          </span>
        )
      }
    },
    {
      id: 'modifiedDate',
      accessor: 'modifiedDate',
      Header: 'Last Modified',
      Cell: ({ row }) => {
        const when = row.original.modifiedDate
        const who = row.original.modifiedBy
        if (!when) return <span className={classes.timestampText}>—</span>
        return (
          <Tooltip title={`${new Date(when).toLocaleString()}${who ? ` by ${who}` : ''}`} arrow>
            <span className={classes.timestampText}>
              {formatRelativeTime(when)}{who ? ` · ${who}` : ''}
            </span>
          </Tooltip>
        )
      }
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { code } = row.original
        return (
          <div style={{ display: 'flex', gap: '2px' }}>
            <Tooltip title='Manage tags' arrow>
              <IconButton size='small' className={classes.actionBtn} onClick={() => setTagModalCode(code)}>
                <LocalOfferIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Duplicate pipeline' arrow>
              <IconButton
                size='small'
                className={classes.actionBtn}
                onClick={() => handleDuplicate(row.original)}
              >
                <FileCopyIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Edit pipeline' arrow>
              <IconButton
                size='small'
                className={classes.actionBtn}
                onClick={() => history.push(`${ADMIN_ROUTES.DENALI.ANALYSIS.DATA_PIPELINES}/${code}`)}
              >
                <EditIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </div>
        )
      }
    }
  ], [classes, history, handleDuplicate])
  /* eslint-enable react/prop-types */

  const defaultSort = useMemo(() => [{ id: 'code', desc: false }], [])

  if (error) {
    return (
      <AdminPage>
        <ErrorDisplay error={error} onRetry={refetch} />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <div className={classes.newBtnWrap}>
        <Link to={ADMIN_ROUTES.DENALI.ANALYSIS.NEW_DATA_PIPELINE}>
          <SydButton size='sm' variant='primary' icon='add'>New Pipeline</SydButton>
        </Link>
      </div>

      {isLoading ? (
        <Loading />
      ) : tableData.length === 0 ? (
        <div style={{ padding: '48px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 'var(--text-size-sm)', color: 'var(--color-neutral-500)', marginBottom: '24px', lineHeight: 1.7 }}>
            No data pipelines yet. Create one to get started.
          </div>
          <Link to={ADMIN_ROUTES.DENALI.ANALYSIS.NEW_DATA_PIPELINE}>
            <SydButton size='sm' variant='primary' icon='add'>New Pipeline</SydButton>
          </Link>
        </div>
      ) : (
        <>
          <div className={classes.toolbar}>
            <span className={classes.pipelineCount}>
              {tableData.length} {tableData.length === 1 ? 'pipeline' : 'pipelines'}
            </span>
            <input
              className={classes.searchInput}
              placeholder='Search pipelines…'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className={classes.tableWrap}>
            <OperationalTable
              variant='blank'
              itemName='Data Pipelines'
              columns={columns}
              data={tableData}
              defaultSort={defaultSort}
              searchText={searchText}
            />
          </div>
        </>
      )}

      {tagModalCode && (
        <DataPipelineTagManagementModal
          code={tagModalCode}
          onClose={() => setTagModalCode(null)}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </AdminPage>
  )
}

export default DataPipelinesHome
